import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Icon, Modal } from '@components/index';
import { allowedCSVSizeForGroupCreation } from '@constants/file';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { priorityOptions } from '@features/lead-management/constants/lead-info';
import useLeadSources from '@features/lead-management/hooks/useLeadSources';
import {
  useCreateCategoryMutation,
  useCreateLeadSourceMutation,
  useCreateTagMutation,
  useGetAgentsQuery,
  useGetCategoriesQuery,
  useGetStatusOptionsQuery,
  useGetTagsQuery,
  useLazyGetCategoriesQuery,
  useLazyGetTagsQuery,
  useLeadBulkCreateMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import {
  LeadAssignee,
  LeadCategory,
  LeadPriority,
  LeadSource,
  LeadStatus,
  LeadTag
} from '@features/lead-management/types/common';
import {
  LeadDataMappingLists,
  LeadUploadRow
} from '@features/lead-management/types/leadBulkCreate';
import { getCities } from '@features/lead-management/utils/common';
import {
  prepareLeadUpload,
  toCreatePayload
} from '@features/lead-management/utils/leadBulkCreate';
import useNotify from '@hooks/notify';
import { parseXLSXFile } from '@utils/file';

import LeadDownloadTemplateSection from './components/LeadDownloadTemplateSection';
import LeadParsedGrid from './components/LeadParsedGrid';
import LeadUploadFileSection from './components/LeadUploadFileSection';

import { BulkCreateLeadResult, CreateLeadRequest } from '../../types/request';

interface LeadBulkUploadModalProps {
  isOpen: boolean;
  handleCancel: () => void;
  organizationId: string;
  onCreateSuccess: () => void;
}

const LeadBulkUploadModal: FC<LeadBulkUploadModalProps> = ({
  isOpen,
  handleCancel,
  organizationId,
  onCreateSuccess
}) => {
  const { currentOrg } = useSelector((state: any) => state.user);
  const { createErrorAlert, createSuccessAlert, createWarningAlert } =
    useNotify();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileUploadError, setFileUploadError] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [rows, setRows] = useState<LeadUploadRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [hasApiErrors, setHasApiErrors] = useState(false);
  const [rowErrorsById, setRowErrorsById] = useState<Record<string, string[]>>(
    {}
  );

  const { data: statusResp } = useGetStatusOptionsQuery({ organizationId });
  const { data: categoriesResp } = useGetCategoriesQuery({ organizationId });
  const { data: agentsResp } = useGetAgentsQuery({ organizationId });
  const { data: tagsResp } = useGetTagsQuery({ organizationId });
  const { sources: leadSourceList, getSourceLabel } = useLeadSources();
  const [leadBulkCreate, { isLoading: isBulkCreating }] =
    useLeadBulkCreateMutation();
  const [createCategory] = useCreateCategoryMutation();
  const [fetchCategories] = useLazyGetCategoriesQuery();
  const [createLeadSource] = useCreateLeadSourceMutation();
  const [createTag] = useCreateTagMutation();
  const [fetchTags] = useLazyGetTagsQuery();

  const resetState = () => {
    setFile(undefined);
    setHeaders([]);
    setRows([]);
    setRowErrorsById({});
    setHasApiErrors(false);
  };

  const sourceMappingList = useMemo(
    () =>
      leadSourceList.map((source) => ({
        id: source.id,
        label: getSourceLabel(source.name),
        value: source.name
      })),
    [leadSourceList, getSourceLabel]
  );

  const sources = useMemo(
    () => sourceMappingList.map((o) => String(o.label)),
    [sourceMappingList]
  );
  const priorities = useMemo(
    () => priorityOptions.map((p) => String(p.label)),
    []
  );
  const statuses = useMemo(
    () =>
      statusResp?.length > 0
        ? statusResp?.map((d: LeadStatus) => String(d.value || ''))
        : [],
    [statusResp]
  );
  const categories = useMemo(
    () =>
      categoriesResp?.length > 0
        ? categoriesResp?.map((c: LeadCategory) => String(c.value || ''))
        : [],
    [categoriesResp]
  );
  const assignees = useMemo(
    () =>
      agentsResp?.sales_agents && agentsResp.sales_agents.length > 0
        ? agentsResp.sales_agents.map((a: LeadAssignee) => String(a.name || ''))
        : [],
    [agentsResp]
  );
  const tags = useMemo(
    () =>
      tagsResp?.length > 0
        ? tagsResp?.map((t: LeadTag) => String(t.name || ''))
        : [],
    [tagsResp]
  );

  const cityList = getCities();

  const handleFileProcessing = async (selectedFile: File) => {
    setFileUploadError('');
    try {
      const lists = {
        statuses,
        sources,
        tags,
        categories,
        priorities,
        assignees,
        cities: cityList
      };
      const {
        headers: inferredHeaders,
        rowsWithId,
        rowErrorsById: newRowErrorsById
      } = await prepareLeadUpload(selectedFile, lists);

      setFile(selectedFile);
      setHeaders(inferredHeaders);
      setRows(rowsWithId);
      setRowErrorsById(newRowErrorsById);
    } catch (error) {
      setFileUploadError('Error parsing file');
    }

    setTimeout(() => {
      setIsLoading(false);
      createSuccessAlert('File uploaded Successfully');
    }, 500);
  };

  const checkFileValidityAndProcess = (selectedFile: File) => {
    if (
      selectedFile?.type !== 'text/csv' &&
      selectedFile.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFileUploadError('Please provide a csv or xlsx file to upload');
      return false;
    }

    // validate file size
    if (selectedFile.size > allowedCSVSizeForGroupCreation) {
      setFileUploadError('Upload failed. File size more than 2MB');
      return false;
    }

    try {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onload = async () => {
        let rowsInFile = 0;
        if (selectedFile.type === 'text/csv') {
          const csvText = await selectedFile.text();
          // csv data includes headers
          rowsInFile = csvText.split('\n').filter((line) => line).length - 1;
        } else {
          const parsedData = await parseXLSXFile(selectedFile);
          rowsInFile = (parsedData.data || []).length;
        }

        if (rowsInFile > 100) {
          setIsLoading(false);
          setFileUploadError('Maximum no. of leads allowed would be 100');
        } else {
          await handleFileProcessing(selectedFile);
        }
      };
      // reading triggers onload
      reader.readAsArrayBuffer(selectedFile);
    } catch (error) {
      setFileUploadError('Error reading file');
      return false;
    }
    return true;
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      checkFileValidityAndProcess(selectedFile);
    }
  };

  const handleCreateLeads = async () => {
    if (!rows.length) {
      createWarningAlert('Please upload a file with at least one lead');
      return;
    }
    try {
      const validEntries = rows
        .map((rowdata, index) => ({ rowdata, index }))
        .filter(({ index }) => !rowErrorsById[`row-${index}`]?.length);

      const existingCategories = new Set(
        (categoriesResp || []).map((c: LeadCategory) => c.value)
      );
      const existingSourceLabels = new Set(
        sourceMappingList.map((s) => String(s.label))
      );
      const existingTagNames = new Set(
        (tagsResp || []).map((t: LeadTag) => t.name)
      );

      const newCategoryNames = new Set<string>();
      const newSourceNames = new Set<string>();
      const newTagNames = new Set<string>();

      validEntries.forEach(({ rowdata }) => {
        const cat = rowdata.Category?.trim();
        if (cat && !existingCategories.has(cat)) {
          newCategoryNames.add(cat);
        }
        const src = rowdata.Source?.trim();
        if (src && !existingSourceLabels.has(src)) {
          newSourceNames.add(src);
        }
        const tag = (
          Array.isArray(rowdata.Tags) ? rowdata.Tags[0] : rowdata.Tags
        )
          ?.toString()
          .trim();
        if (tag && !existingTagNames.has(tag)) {
          newTagNames.add(tag);
        }
      });

      const [categoryList, sourceList, tagsList] = await Promise.all([
        (async () => {
          if (newCategoryNames.size === 0) {
            return categoriesResp as LeadCategory[];
          }
          await Promise.allSettled(
            [...newCategoryNames].map((name) =>
              createCategory({ organizationId, name }).unwrap()
            )
          );
          const refreshed = await fetchCategories({
            organizationId
          }).unwrap();
          return refreshed?.length > 0
            ? (refreshed as LeadCategory[])
            : (categoriesResp as LeadCategory[]);
        })(),
        (async () => {
          if (newSourceNames.size === 0) {
            return sourceMappingList as LeadSource[];
          }
          const sourceNamesArr = [...newSourceNames];
          const results = await Promise.allSettled(
            sourceNamesArr.map((name) =>
              createLeadSource({ organizationId, name }).unwrap()
            )
          );
          const createdSources = results.reduce<LeadSource[]>((acc, r, i) => {
            if (r.status === 'fulfilled' && r.value) {
              const created = r.value as {
                id: string;
                name: string;
              };
              acc.push({
                id: created.id,
                label: sourceNamesArr[i],
                value: created.name
              });
            }
            return acc;
          }, []);
          return [...sourceMappingList, ...createdSources];
        })(),
        (async () => {
          if (newTagNames.size === 0) {
            return tagsResp as LeadTag[];
          }
          await Promise.allSettled(
            [...newTagNames].map((name) =>
              createTag({ organizationId, name, color: '#F2E1E9' }).unwrap()
            )
          );
          const refreshed = await fetchTags({ organizationId }).unwrap();
          return refreshed?.length > 0
            ? (refreshed as LeadTag[])
            : (tagsResp as LeadTag[]);
        })()
      ]);

      const leadDataMappingLists: LeadDataMappingLists = {
        statusList: statusResp as LeadStatus[],
        sourceList,
        tagsList,
        categoryList,
        priorityList: priorityOptions as LeadPriority[],
        assigneeList: agentsResp?.sales_agents as LeadAssignee[]
      };

      const payloads: CreateLeadRequest[] = validEntries.map(({ rowdata }) =>
        toCreatePayload(rowdata, leadDataMappingLists)
      );

      // leadbulk create api call
      const res = await leadBulkCreate({ organizationId, body: payloads });

      if ('data' in res && Array.isArray(res.data)) {
        const apiResults = res.data as BulkCreateLeadResult[];
        const failedResults = apiResults.filter(
          (r) => r.errors && r.errors.length > 0
        );

        if (failedResults.length > 0) {
          const updatedErrors = { ...rowErrorsById };
          apiResults.forEach((result, idx) => {
            if (result.errors?.length > 0) {
              const originalIndex = validEntries[idx]?.index;
              if (originalIndex !== undefined) {
                const rowId = `row-${originalIndex}`;
                updatedErrors[rowId] = [
                  ...(updatedErrors[rowId] || []),
                  ...result.errors.map(
                    (e) => e.charAt(0).toUpperCase() + e.slice(1)
                  )
                ];
              }
            }
          });
          setRowErrorsById(updatedErrors);
          setHasApiErrors(true);
          createErrorAlert(
            'Error adding leads. Please check the file and try again.',
            { classOverride: 'max-w-xl' }
          );
        }
      } else if (
        'data' in res &&
        (res.data as { status: string })?.status === 'success'
      ) {
        const validCount = payloads.length;
        sendMixpanelEvent(Event.LEAD_CSV_PROCESSED, {
          org_id: organizationId,
          org_name: currentOrg?.name,
          file_name: file?.name,
          total_rows: rows.length,
          leads_created: validCount,
          leads_skipped: rows.length - validCount,
          validation_errors: rowErrorsById,
          file_size: file?.size
        });
        createSuccessAlert('Leads added successfully');
        onCreateSuccess();
        resetState();
        handleCancel();
      } else {
        createErrorAlert('Error adding leads');
      }
    } catch (err: any) {
      createErrorAlert(err?.data?.message || 'Error adding leads');
    }
  };

  const onCancelClick = () => {
    resetState();
    if (!file) {
      handleCancel();
    }
  };

  const handleCloseModal = () => {
    resetState();
    handleCancel();
  };

  // limit preview for sidebar-like list if needed
  const parsedRowsPreview = useMemo(() => rows.slice(0, 5), [rows]);

  const totalErrorRows = Object.values(rowErrorsById).filter(
    (errors) => errors.length > 0
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleCloseModal}
      isCloseIconRequired
      isClickOutsideRequired={!file}
    >
      <div className="w-225 min-w-125 py-6">
        <div className="mb-3 px-6 text-20 font-medium leading-[28px] tracking-normal text-[#1C1C1D]">
          {file?.name ? file.name : 'Upload CSV/Excel'}
        </div>
        <div className="mt-6 flex flex-col gap-y-2 border-y border-[#E4E4E4] px-6 py-3">
          {!file && (
            <div className="flex items-center justify-between">
              <div className="font-dm-sans text-sm font-normal not-italic leading-5 tracking-normal text-[#7E7E7E]">
                Upload a CSV to quickly import user data
              </div>
              <LeadDownloadTemplateSection
                isFileAvailable={!!file}
                tableHeaders={headers}
                tableRows={parsedRowsPreview as any[]}
                statuses={statuses}
                sources={sources}
                tags={tags}
                categories={categories}
                priorities={priorities}
                assignees={assignees}
                cities={cityList}
              />
            </div>
          )}

          {!file && (
            <LeadUploadFileSection
              file={file}
              checkFileValidityAndProcess={checkFileValidityAndProcess}
              isLoading={isLoading}
              onFileSelect={onFileSelect}
            />
          )}
          {!!rows.length && (
            <LeadParsedGrid
              headers={headers}
              rows={rows as any[]}
              rowErrorsById={rowErrorsById}
              totalErrorRows={totalErrorRows}
            />
          )}
        </div>
        <div className="mt-3 flex justify-between gap-x-4 px-6">
          <div
            className="flex items-center gap-x-2"
            style={{ opacity: totalErrorRows > 0 ? 1 : 0 }}
          >
            <Icon name="alert-circle" className="!size-6" stroke="#CF392E" />
            <div className="text-sm font-normal not-italic leading-none tracking-normal text-error">
              The rows marked in red will not be added to Lead list
            </div>
          </div>
          <div className="flex items-center gap-x-4">
            <Button
              variant="secondary"
              className="!w-20"
              handleClick={onCancelClick}
              disabled={isBulkCreating}
              label="Cancel"
            />
            <Button
              variant="primary"
              className="!w-28"
              handleClick={handleCreateLeads}
              disabled={
                !rows.length ||
                !!fileUploadError ||
                isBulkCreating ||
                hasApiErrors
              }
              label={isBulkCreating ? 'Adding…' : 'Add'}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LeadBulkUploadModal;
